import { useFormik } from "formik";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useLazyOrganizationUsersQuery,
  useOrganizationUsersQuery,
} from "../../api/network/adminApiServices";
import { useCreateChatChannelMutation } from "../../api/network/chatApiServices";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { ChatCreateNewPayload } from "../../api/types/Chat";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import {
  LegacyUserEntityListingPayload,
  TLegacyUser,
  TLegacyUserWithAccount,
} from "../../api/types/User";
import {
  AdminFormFieldAsyncDropdown,
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldInput,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  PillItem,
  TSelectboxOption,
} from "../../components/admin/formFields";
import { APP_CONFIG } from "../../constants/constants";
import { useLoggedInUserData } from "../../utils/user";
import { useChatContext } from "./contexts/chat.context";
import {
  createNewGroupChatInitialValues,
  createNewGroupChatValidationSchema,
  TFormFieldNames,
} from "./validation";
import FormActionButtons from "../../components/admin/formActionButtons";
import { asyncDebounce } from "../../utils/common";
import { GroupBase, OptionsOrGroups } from "react-select";

type ModalCreateNewChatGroupProps = {};

const ModalCreateNewChatGroup: FC<ModalCreateNewChatGroupProps> = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "chatSection.create_new",
  });
  const dispatch = useDispatch();

  const { setSelectedChatChannel } = useChatContext();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );

  // create chat channel mutation
  const [
    createChatChannelAPITrigger,
    { isLoading: isLoadingCreateChatChannel },
  ] = useCreateChatChannelMutation();

  //formik
  const formik = useFormik({
    initialValues: createNewGroupChatInitialValues,
    validationSchema: createNewGroupChatValidationSchema,
    onSubmit: (values) => {
      //prepare payload
      const payload: ChatCreateNewPayload = {
        channel_type: values.selected_users.length > 1 ? 2 : 1,
        channel_name: values.channel_name,
        members: values.selected_users.map((item) =>
          typeof item === "string" ? item : item.value
        ),
      };
      createChatChannelAPITrigger(payload)
        .unwrap()
        .then((data) => {
          if (APP_CONFIG.DEBUG.MESSAGING) console.log(">>>", data);
          formik.resetForm();
          if (!!data?.id) {
            // changes the checkcase condition
            dispatch(
              setModalsData({ ...modalsState, showCreateGroupChatModal: false })
            );
            toast.success(t("toast.create_success"));
            setSelectedChatChannel(data.channel_id);
          } else {
            toast.error(t("toast.create_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.create_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  //formik
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
    setFieldTouched,
    resetForm,
    submitForm,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t("submit_button"),
              variant: "primary",
              disabled:
                !isFormValid ||
                isLoadingCreateChatChannel ||
                values.selected_users.length === 0,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isLoadingCreateChatChannel,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [isFormValid, isLoadingCreateChatChannel, values.selected_users]);
  const hideModal = () => {
    formik.resetForm();
    dispatch(
      setModalsData({ ...modalsState, showCreateGroupChatModal: false })
    );
  };

  // prepare user query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgUsersQueryParams, setOrgUsersQueryParams] =
    useState<LegacyUserEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      search: "",
    });

  // fetch organization users
  const { data: dataOrgUsers, isFetching: isFetchingOrgUsers } =
    useOrganizationUsersQuery(orgUsersQueryParams);

  const [orgUsersQuery, {}] = useLazyOrganizationUsersQuery();

  const loadOptionsHandlerUser = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: driversQueryResponse } = await orgUsersQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page || 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.USER_DROPDOWN.join(","), // search by userid only
    });

    if (!driversQueryResponse || !driversQueryResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }
    const { results } = driversQueryResponse as {
      results: TLegacyUserWithAccount[];
    };

    const options = !!results
      ? (results || ([] as TLegacyUserWithAccount[]))
          .filter(
            (item: TLegacyUserWithAccount) =>
              item.userid !== loggedInUserData?.user?.userid
          ) // exclude current user
          .filter(
            (item: TLegacyUserWithAccount) =>
              !values.selected_users
                .map((item) => (typeof item === "string" ? item : item.value))
                .includes(`${item.userid}`)
          ) // exclude already selected users
          .map(
            (item: TLegacyUserWithAccount, index: number) =>
              ({
                value: `${item.userid}`,
                label: `${item.userid}`,
              }) as TSelectboxOption
          )
      : [];

    return {
      options: options,
      hasMore: driversQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerUser = asyncDebounce(
    loadOptionsHandlerUser,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  if (modalsState.showCreateGroupChatModal === false) return null;
  return (
    <>
      <div className="justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 z-overlay outline-none focus:outline-none">
        {/* <div
          className="fixed w-full h-screen bg-modal-overlay z-overlay"
          onClick={hideModal}
        ></div> */}
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[480px] z-modal">
          <form
            className="p-8 bg-gray-100 grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
            onSubmit={handleSubmit}
          >
            <h2 className="col-span-6 text-2xl font-bold text-heading-black">
              {t("heading")}
            </h2>
            <>
              <AdminFormFieldInput
                label={t("channel_name")}
                type="text"
                id="ChannelName"
                name="channel_name"
                placeholder={t("channel_name_placeholder")}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.channel_name}
                touched={touched.channel_name}
                error={errors.channel_name}
              />

              <div className="col-span-6 grid grid-cols-6 gap-6">
                <AdminFormFieldAsyncLazyDropdown
                  key={`user_selector_`} // causes re-render on group selection
                  loadingData={isFetchingOrgUsers}
                  label={t("select_user_to_add")}
                  id="users"
                  name="users"
                  isMulti={true}
                  value={values.selected_users as any}
                  noOptionsMessage={() => t("user_selector_no_options")}
                  // defaultOption={{ value: `${values.group_selector}`, label: `${groupDisplayName(`${values.streetaddress}`, "-")}` }}
                  defaultOptions={dataOrgUsers?.results
                    ?.filter(
                      (item: TLegacyUser) =>
                        item.userid !== loggedInUserData?.user?.userid
                    ) // exclude current user
                    ?.filter(
                      (item) =>
                        !values.selected_users
                          .map((item) =>
                            typeof item === "string" ? item : item?.value
                          )
                          .includes(`${item.userid}`)
                    ) // exclude already selected users
                    ?.map((item: TLegacyUser) => ({
                      value: `${item.userid}`,
                      label: item.displayname || item.userid,
                    }))}
                  placeholder={t("select_users")}
                  loadOptions={debouncedLoadOptionsHandlerUser}
                  onChange={(e: any) => {
                    if (!!e) {
                      const newlySelected = e;
                      setFieldValue("selected_users", [...newlySelected]);
                    }
                  }}
                  // onBlur={(e) => {
                  //   formikSetTouched("streetaddress", true);
                  //   handleBlur(e);
                  // }}
                  // error={errors.users}
                  // touched={touched.users}
                  // disabled={!userCanEdit || loadingData || values.all_groups}
                />
                {/* <AdminFormFieldPillList>
                  {values.selected_users.map((user: string, i: number) => (
                    <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                      key={`pillItem_${i}`}
                      item={{ name: user } as PillItem}
                      onRemove={() => {
                        setFieldValue(
                          "selected_users",
                          values.selected_users.filter(
                            (selectedUser: any) => selectedUser !== user
                          )
                        );
                      }}
                      disabled={false}
                    />
                  ))}
                </AdminFormFieldPillList> */}
                {errors.selected_users && (
                  <span className="col-span-12 bg-field-error-light px-2 py-1 -mt-4 rounded text-field-error-dark text-xs">
                    {errors.selected_users}
                  </span>
                )}
              </div>
              {MemoizedFormActionButtons}
              {/* <AdminFormFieldSubmit
                label={t('submit_button')}
                variant="primary"
                disabled={!isFormValid || isLoadingCreateChatChannel || values.selected_users.length === 0}
              />

              <AdminFormFieldSubmit
                label={tMain('cancel')}
                type="button"
                variant="danger-transparent"
                disabled={isLoadingCreateChatChannel}
                onClick={hideModal}
              /> */}
            </>
          </form>
        </div>
      </div>
    </>
  );
};

export default ModalCreateNewChatGroup;
